import dayjs, { type Dayjs } from 'dayjs';
import type { OrganizationPublisherOut } from '@/api/generated/schemas';
import { isDisabledStartDate } from '@/components/tenancy/contractFormValues';
import type { PublisherTreeSource } from '@/components/user_catalog/publishers/publisherTree';

export type OrganizationPublisherLinkStatus = 'scheduled' | 'active' | 'ended';

export type ResolvedOrganizationPublisherLink = OrganizationPublisherOut & {
  status: OrganizationPublisherLinkStatus;
  /** The publisher's following link, in any organization — it ends this one
   * on its `valid_from`. A link carries no end date of its own. */
  next: OrganizationPublisherOut | null;
};

/**
 * Places each link of an organization on the timeline its publisher follows.
 *
 * Mirrors the backend: on a given day a publisher belongs to the organization
 * of its link with the latest `valid_from` on or before that day, so a link
 * ends only when the publisher's next one starts. `allLinks` must hold every
 * link of the publishers in `orgLinks`, other organizations included —
 * without them a publisher that moved away would still read as active.
 *
 * A link has started once its `valid_from` day has come — the boundary the
 * backend draws (and `isContractEditable` uses).
 */
export const resolveOrganizationPublisherLinks = (
  orgLinks: OrganizationPublisherOut[],
  allLinks: OrganizationPublisherOut[],
  today: Dayjs = dayjs()
): ResolvedOrganizationPublisherLink[] => {
  const byPublisher = new Map<number, OrganizationPublisherOut[]>();
  for (const link of [...orgLinks, ...allLinks]) {
    const links = byPublisher.get(link.publisher_id) ?? [];
    // Both lists overlap — the organization's own links come back in both.
    if (!links.some((other) => other.id === link.id)) links.push(link);
    byPublisher.set(link.publisher_id, links);
  }
  for (const links of byPublisher.values()) {
    links.sort((a, b) => a.valid_from.localeCompare(b.valid_from));
  }

  const hasStarted = (link: OrganizationPublisherOut) =>
    !dayjs(link.valid_from).isAfter(today, 'day');

  return orgLinks.map((link) => {
    const next =
      byPublisher
        .get(link.publisher_id)
        ?.find((other) => other.valid_from > link.valid_from) ?? null;
    const status: OrganizationPublisherLinkStatus = !hasStarted(link)
      ? 'scheduled'
      : next && hasStarted(next)
        ? 'ended'
        : 'active';
    return { ...link, status, next };
  });
};

/**
 * Publishers in the organization now or joining it later — what the backend
 * accepts for a member narrowed to a publisher. Contracts check membership on
 * their own start, which this set is the best guess for.
 */
export const upcomingPublisherIds = (
  links: ResolvedOrganizationPublisherLink[]
): number[] => [
  ...new Set(
    links
      .filter((link) => link.status !== 'ended')
      .map((link) => link.publisher_id)
  ),
];

/** Antd reads `children` off a row to nest it. Leaves carry no key at all —
 * an empty array would draw an expander with nothing behind it. */
export type OrganizationPublisherLinkRow = ResolvedOrganizationPublisherLink & {
  children?: OrganizationPublisherLinkRow[];
};

/**
 * Nests each imprint's link under the link of its parent it depends on.
 *
 * A publisher may hold several links in one organization (it left and came
 * back), so the host is the parent's link in force here on the day the
 * imprint's link starts — the backend admits an imprint only then. With no
 * such link (the parent is not here, or publishers have not loaded yet) the
 * imprint's link stays at the top rather than dropping out: the tab counts
 * every link, and a vanishing row would contradict it.
 */
export const buildOrganizationPublisherLinkTree = (
  links: ResolvedOrganizationPublisherLink[],
  publishers: PublisherTreeSource[]
): OrganizationPublisherLinkRow[] => {
  const publisherById = new Map(publishers.map((p) => [p.id, p]));
  const linksByPublisher = new Map<
    number,
    ResolvedOrganizationPublisherLink[]
  >();
  for (const link of links) {
    const own = linksByPublisher.get(link.publisher_id);
    if (own) own.push(link);
    else linksByPublisher.set(link.publisher_id, [link]);
  }

  const hostOf = (link: ResolvedOrganizationPublisherLink) => {
    const parentId = publisherById.get(link.publisher_id)?.parent_id;
    if (parentId == null) return undefined;
    return (linksByPublisher.get(parentId) ?? [])
      .filter((candidate) => candidate.valid_from <= link.valid_from)
      .reduce<
        ResolvedOrganizationPublisherLink | undefined
      >((latest, candidate) => (!latest || candidate.valid_from > latest.valid_from ? candidate : latest), undefined);
  };

  const childrenByHost = new Map<number, ResolvedOrganizationPublisherLink[]>();
  const roots: ResolvedOrganizationPublisherLink[] = [];
  for (const link of links) {
    const host = hostOf(link);
    // A publisher listed as its own parent would host itself.
    if (!host || host.id === link.id) {
      roots.push(link);
      continue;
    }
    const siblings = childrenByHost.get(host.id);
    if (siblings) siblings.push(link);
    else childrenByHost.set(host.id, [link]);
  }

  // A publisher's links stay together, newest first.
  const nameOf = (link: ResolvedOrganizationPublisherLink) =>
    publisherById.get(link.publisher_id)?.name ?? '';
  const order = (
    a: ResolvedOrganizationPublisherLink,
    b: ResolvedOrganizationPublisherLink
  ) =>
    nameOf(a).localeCompare(nameOf(b)) ||
    a.publisher_id - b.publisher_id ||
    b.valid_from.localeCompare(a.valid_from);

  const placed = new Set<number>();
  const toRows = (
    level: ResolvedOrganizationPublisherLink[]
  ): OrganizationPublisherLinkRow[] =>
    [...level].sort(order).map((link) => {
      placed.add(link.id);
      const children = toRows(childrenByHost.get(link.id) ?? []);
      return { ...link, ...(children.length > 0 && { children }) };
    });

  const tree = toRows(roots);

  // A parent cycle, which the schema does not forbid, hosts its links in
  // each other and leaves them unreachable from the top. Surface them flat.
  const unreachable = links
    .filter((link) => !placed.has(link.id))
    .sort(order)
    .map((link) => ({ ...link }));

  return [...tree, ...unreachable];
};

/**
 * Days a new link cannot start on: today or earlier (`isDisabledStartDate`),
 * and for an imprint also any day its parent — `host` — is not in the
 * organization, i.e. before the host starts or once the parent's next link
 * has moved it away.
 */
export const isDisabledLinkDate = (
  date: Dayjs,
  host?: ResolvedOrganizationPublisherLink
) =>
  isDisabledStartDate(date) ||
  (host != null &&
    (date.isBefore(host.valid_from, 'day') ||
      (host.next != null && !date.isBefore(host.next.valid_from, 'day'))));
